import { inject, autoinject, LogManager, bindable } from 'aurelia-framework';
import { DialogController } from 'aurelia-dialog';
import { Logger } from 'aurelia-logging';
import { Endpoint, Rest } from 'aurelia-api';
import { ControllerValidateResult, ValidationController, ValidationControllerFactory } from 'aurelia-validation';
import { BootstrapFormRenderer } from '../../services/bootstrap-form-renderer';
import { Helpers } from '../../services/helpers';
import { NotificationViewModel } from '../../models/notificationview-model';
import { NotificationEditViewModel } from '../../models/notificationeditview-model';
import { SchoolViewModel } from '../../models/schoolview-model';
import { CourseViewModel } from '../../models/courseview-model';

@autoinject
export class NotificationsModalUpdate {
    @bindable id: number = 0;
    model: NotificationEditViewModel = null;
    private logger: Logger = LogManager.getLogger('NOTIFICATIONMODALUPDATE');
    private validationController: ValidationController | null = null;
    private saveDisabled: boolean = false;
    private schools: Array<SchoolViewModel> = [];
    private courses: Array<CourseViewModel> = [];

    constructor(@inject(Endpoint.of('api')) private api: Rest,
        private validationControllerFactory: ValidationControllerFactory,
        private dialogController: DialogController) {
        this.validationController = validationControllerFactory.createForCurrentScope();

        this.validationController.addRenderer(new BootstrapFormRenderer());
    }

    async activate(params: any) {
        this.logger.debug('activate');
        this.id = params.notificationId;

        await this.getSchools();
        await this.getCourses();
        await this.getModel();
    }

    async getSchools() {
        this.logger.debug('getSchools');

        await this.api.find('/School/GetAll')
            .then((data: Array<SchoolViewModel>) => {
                this.schools = data;
                this.logger.debug('Success getSchools: ', data);
            }).catch(async (err) => {
                this.logger.debug('Error getSchools: ', await err.json());
                await Helpers.ParseValidationError(err, this.validationController, this.model);
            });
    }

    async getCourses() {
        this.logger.debug('getCourses');

        await this.api.find('/Course/GetAll')
            .then((data: Array<CourseViewModel>) => {
                this.courses = data;
                this.logger.debug('Success getCourses: ', data);
            }).catch(async (err) => {
                this.logger.debug('Error getCourses: ', await err.json());
                await Helpers.ParseValidationError(err, this.validationController, this.model);
            });
    }

    async getModel() {
        this.logger.debug('getModel');

        await this.api.find('/Notification/GetById', this.id)
            .then(async (data: NotificationViewModel) => {
                this.logger.debug('Success getModel: ', data);
                this.model = new NotificationViewModel(data);
            }).catch(async (err) => {
                this.logger.debug('Error getModel: ', err);
                await Helpers.ParseValidationError(err, this.validationController, this.model);
            });
    }

    async save() {
        this.logger.debug('save');
        this.saveDisabled = true;

        const validation: ControllerValidateResult = await this.validationController.validate();

        if (validation) {
            await this.api.update('/Notification/Update', this.id, this.model)
                .then((data: NotificationEditViewModel) => {
                    this.logger.debug('Success save: ' + data);
                    this.saveDisabled = false;
                    this.dialogController.ok(data);
                }).catch(async (err) => {
                    this.saveDisabled = false;
                    this.logger.debug('Error save: ', err);
                    await Helpers.ParseValidationError(err, this.validationController, this.model);
                });
        }
    }

    cancel() {
        this.dialogController.cancel();
    }
}